import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ShipmentMetadata } from './shipment-metadata';
import { ChargesTable } from '@/components/common/charges-table';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { SystemEntity } from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import type { GetShipmentResponse } from '@erp/rpc';

type ShipmentWithDetails = NonNullable<GetShipmentResponse['shipment']>;

export interface ShipmentDetailsProps {
  shipment: ShipmentWithDetails;
  salesOrders?: GetShipmentResponse['salesOrders'];
  purchaseOrders?: GetShipmentResponse['purchaseOrders'];
  chargesCount?: number;
}

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return '-';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

export function ShipmentDetails({
  shipment,
  salesOrders = [],
  purchaseOrders = [],
  chargesCount = 0,
}: ShipmentDetailsProps) {
  // Fetch charges for the shipment
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.SCM_SHIPMENT,
    entityId: shipment.id,
    includeLineCharges: false, // Only header charges
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  const hasSalesOrders = salesOrders.length > 0;
  const hasPurchaseOrders = purchaseOrders.length > 0;
  const hasCharges = headerCharges.length > 0;
  const hasTabs = hasSalesOrders || hasPurchaseOrders || hasCharges;

  return (
    <div className="h-full flex flex-col space-y-4 overflow-y-auto px-3">
      <ShipmentMetadata shipment={shipment} chargesCount={chargesCount} />

      {/* Tabs for Related Entities and Charges */}
      {hasTabs && (
        <>
          <Separator />
          <Tabs
            defaultValue={
              hasSalesOrders
                ? 'sales-orders'
                : hasPurchaseOrders
                  ? 'purchase-orders'
                  : 'charges'
            }
            className="w-full"
          >
            <TabsList className="w-full">
              {hasSalesOrders && (
                <TabsTrigger value="sales-orders">
                  Sales Orders ({salesOrders.length})
                </TabsTrigger>
              )}
              {hasPurchaseOrders && (
                <TabsTrigger value="purchase-orders">
                  Purchase Orders ({purchaseOrders.length})
                </TabsTrigger>
              )}
              {hasCharges && (
                <TabsTrigger value="charges">
                  Charges ({headerCharges.length})
                </TabsTrigger>
              )}
            </TabsList>

            {hasSalesOrders && (
              <TabsContent value="sales-orders" className="mt-0">
                <div className="space-y-2 p-3">
                  {salesOrders.map((so) => (
                    <div
                      key={so.id}
                      className="flex items-center justify-between p-2 rounded-md border"
                    >
                      <div className="flex items-center gap-3">
                        <SystemEntityInline
                          entityType={SystemEntity.AR_SALES_ORDER}
                          entityId={so.id}
                        />
                        {so.customerName && (
                          <span className="text-sm text-muted-foreground">
                            {so.customerName}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium">
                        {formatCurrencyValue(so.totalAmount)}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}

            {hasPurchaseOrders && (
              <TabsContent value="purchase-orders" className="mt-0">
                <div className="space-y-2 p-3">
                  {purchaseOrders.map((po) => (
                    <div
                      key={po.id}
                      className="flex items-center justify-between p-2 rounded-md border"
                    >
                      <div className="flex items-center gap-3">
                        <SystemEntityInline
                          entityType={SystemEntity.INTERNAL_PURCHASE_ORDER}
                          entityId={po.id}
                        />
                        {po.supplierName && (
                          <span className="text-sm text-muted-foreground">
                            {po.supplierName}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium">
                        {formatCurrencyValue(po.totalAmount)}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}

            {hasCharges && (
              <TabsContent value="charges" className="mt-0">
                <div className="px-3">
                  <ChargesTable charges={headerCharges} currency="USD" />
                </div>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}
